// src/pages/SmsYonetimi.js

import React, { useState, useEffect } from 'react';
import { db, functions } from '../firebase';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { Box, Typography, Paper, Grid, Switch, FormControlLabel, TextField, Button, CircularProgress, Alert, Divider, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

// Varsayılan Ayarlar: Veritabanında veri yoksa bunlar kullanılır.
const defaultTriggers = {
    workOrderCreated: {
        enabled: true,
        template: "Sayin {musteriAdi}, {aracPlaka} plakali araciniz teslim alinmistir. Ongorulen teslim tarihi: {teslimTarihi}. Detailing Dukkani 216"
    },
    workOrderProcessing: {
        enabled: true,
        template: "Sayin {musteriAdi}, {aracPlaka} plakali araciniz isleme alinmistir. Detailing Dukkani 216"
    },
    workOrderReady: {
        enabled: true,
        template: "Sayin {musteriAdi}, {aracPlaka} plakali araciniz teslimata hazirdir. Sizi bayimize bekliyoruz. Detailing Dukkani 216"
    },
    workOrderDelivered: {
        enabled: true,
        template: "Sayin {musteriAdi}, bizi tercih ettiginiz icin tesekkur ederiz. Iyi gunlerde kullanin. Detailing Dukkani 216"
    },
    // YENİ: PPF Takip SMS'i - Sadece PPF işlemi yapılan araçlar için
    ppfFollowUp: {
        enabled: true,
        template: "Sayin {musteriAdi}, PPF kaplamaniz 14 gun once uygulanmistir. Herhangi bir sorun yasarsaniz bizi arayin. Detailing Dukkani 216",
        delayDays: 14,
        sendAtHour: 10,
        onlyForPPF: true
    },
    appointmentCreated: {
        enabled: true,
        template: "Sayin {musteriAdi}, randevunuz olusturulmustur. Tarih: {randevuTarihi}. Detailing Dukkani 216"
    },
    appointmentReminder1Day: {
        enabled: true,
        template: "Sayin {musteriAdi}, yarin saat {randevuSaati}'de randevunuz bulunmaktadir. Detailing Dukkani 216"
    },
    appointmentReminderSameDay: {
        enabled: true,
        template: "Sayin {musteriAdi}, bugun saat {randevuSaati}'de randevunuz bulunmaktadir. Detailing Dukkani 216"
    },
    paymentReminder: {
        enabled: false,
        template: "Sayin {musteriAdi}, {vadeTarihi} vadeli {tutar} tutarindaki odemenizi hatirlatmak isteriz. Detailing Dukkani 216"
    }
};

const SmsYonetimi = () => {
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [testMessage, setTestMessage] = useState('');
    const [testResult, setTestResult] = useState({ status: '', message: '' });
    const [isTesting, setIsTesting] = useState(false);

    useEffect(() => {
        const docRef = doc(db, 'settings', 'sms');
        const unsubscribe = onSnapshot(docRef, (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data();
                // GÜVENLİ BİRLEŞTİRME (Merge Logic)
                const mergedTriggers = { ...defaultTriggers };

                if (data.triggers) {
                    Object.keys(defaultTriggers).forEach(key => {
                        if (data.triggers[key]) {
                            mergedTriggers[key] = {
                                ...defaultTriggers[key],
                                ...data.triggers[key]
                            };
                        }
                    });
                }

                // LEGACY MIGRATION: Eğer veritabanında netgsm ayarı yoksa, build-time'da alınan eski config'i öner
                const legacyNetgsm = {
                    username: "8503033941",
                    password: "De135246@",
                    header: "8503033941"
                };

                const mergedNetgsm = data.netgsm || legacyNetgsm;

                setSettings({
                    ...data,
                    triggers: mergedTriggers,
                    netgsm: mergedNetgsm
                });
            } else {
                // Belge hiç yoksa
                const legacyNetgsm = {
                    username: "8503033941",
                    password: "De135246@",
                    header: "8503033941"
                };
                setSettings({
                    triggers: defaultTriggers,
                    netgsm: legacyNetgsm
                });
            }
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    const handleSwitchChange = (triggerKey) => {
        setSettings(prev => {
            // Eğer veritabanında bu trigger henüz yoksa varsayılandan al (Hata önleyici)
            const currentTrigger = prev.triggers[triggerKey] || defaultTriggers[triggerKey];
            return {
                ...prev,
                triggers: {
                    ...prev.triggers,
                    [triggerKey]: {
                        ...currentTrigger,
                        enabled: !currentTrigger.enabled
                    }
                }
            };
        });
    };

    const handleTemplateChange = (triggerKey, value) => {
        setSettings(prev => {
            const currentTrigger = prev.triggers[triggerKey] || defaultTriggers[triggerKey];
            return {
                ...prev,
                triggers: {
                    ...prev.triggers,
                    [triggerKey]: {
                        ...currentTrigger,
                        template: value
                    }
                }
            };
        });
    };

    const handleSaveSettings = async () => {
        setIsSaving(true);
        try {
            // Veritabanına kaydederken 'merge: true' kullanarak mevcut veriyi koruyoruz
            await setDoc(doc(db, 'settings', 'sms'), settings, { merge: true });
            alert('Ayarlar başarıyla kaydedildi!');
        } catch (error) {
            console.error("Ayarlar kaydedilirken hata:", error);
            alert('Ayarlar kaydedilirken bir hata oluştu.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleSendTestSms = async () => {
        if (!testMessage) { alert('Lütfen test için bir mesaj girin.'); return; }
        setIsTesting(true);
        setTestResult({ status: '', message: '' });
        try {
            const sendTestSms = httpsCallable(functions, 'sendTestSms');
            const result = await sendTestSms({ message: testMessage });
            setTestResult({ status: 'success', message: result.data.message });
        } catch (error) {
            console.error("Test SMS hatası:", error);
            // Hata mesajını kullanıcıya daha anlaşılır göstermek için
            const errorMessage = error.message || "Bilinmeyen bir hata oluştu.";
            setTestResult({ status: 'error', message: errorMessage });
        } finally {
            setIsTesting(false);
        }
    };

    if (loading || !settings) { return <CircularProgress sx={{ display: 'block', margin: 'auto', mt: 4 }} />; }

    const SettingItem = ({ title, triggerKey, variables }) => {
        // Güvenli erişim: Ayarlar yüklenirken triggerKey yoksa hata vermesin
        const trigger = settings.triggers[triggerKey] || defaultTriggers[triggerKey];

        return (
            <Box sx={{ mb: 2 }}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={trigger.enabled}
                            onChange={() => handleSwitchChange(triggerKey)}
                        />
                    }
                    label={title}
                />
                {trigger.enabled && (
                    <TextField
                        fullWidth multiline rows={2} size="small"
                        label="Mesaj Şablonu"
                        value={trigger.template || ''}
                        onChange={(e) => handleTemplateChange(triggerKey, e.target.value)}
                        helperText={`Değişkenler: ${variables}`}
                        sx={{ mt: 1 }}
                    />
                )}
                <Divider sx={{ my: 1 }} />
            </Box>
        );
    };

    return (
        <Box>
            <Typography variant="h4" component="h1" gutterBottom>SMS Yönetim Paneli</Typography>
            <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                    <Paper sx={{ p: 3, mb: 3 }}>
                        <Typography variant="h6" gutterBottom>NetGSM API Ayarları</Typography>
                        <Alert severity="info" sx={{ mb: 2 }}>
                            SMS gönderimi yapabilmek için NetGSM kullanıcı bilgilerinizi buradan girebilirsiniz.
                        </Alert>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="NetGSM Kullanıcı Adı (850xxxxxxx)"
                                    value={settings?.netgsm?.username || ''}
                                    onChange={(e) => setSettings(prev => ({
                                        ...prev,
                                        netgsm: { ...prev.netgsm, username: e.target.value }
                                    }))}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    type="password"
                                    label="NetGSM Şifresi"
                                    value={settings?.netgsm?.password || ''}
                                    onChange={(e) => setSettings(prev => ({
                                        ...prev,
                                        netgsm: { ...prev.netgsm, password: e.target.value }
                                    }))}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="SMS Başlığı (Header)"
                                    value={settings?.netgsm?.header || ''}
                                    onChange={(e) => setSettings(prev => ({
                                        ...prev,
                                        netgsm: { ...prev.netgsm, header: e.target.value }
                                    }))}
                                    helperText="Örn: SOLARGARD"
                                />
                            </Grid>
                        </Grid>
                    </Paper>

                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>Otomatik Bildirim Kuralları</Typography>

                        <Accordion defaultExpanded>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography fontWeight="bold">Araç Durum Bildirimleri</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <SettingItem title="İş Emri Oluşturulduğunda (Teslim Alındı)" triggerKey="workOrderCreated" variables="{musteriAdi}, {aracPlaka}, {teslimTarihi}" />
                                <SettingItem title="Araç İşleme Alındığında" triggerKey="workOrderProcessing" variables="{musteriAdi}, {aracPlaka}" />
                                <SettingItem title="Araç Teslime Hazır Olduğunda" triggerKey="workOrderReady" variables="{musteriAdi}, {aracPlaka}" />
                                <SettingItem title="Araç Teslim Edildiğinde" triggerKey="workOrderDelivered" variables="{musteriAdi}" />
                            </AccordionDetails>
                        </Accordion>

                        <Accordion>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography fontWeight="bold">Randevu Hatırlatmaları</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <SettingItem title="Randevu Oluşturulduğunda (Anlık)" triggerKey="appointmentCreated" variables="{musteriAdi}, {randevuTarihi}" />
                                <SettingItem title="Randevudan 1 Gün Önce" triggerKey="appointmentReminder1Day" variables="{musteriAdi}, {randevuSaati}" />
                                <SettingItem title="Randevu Günü (Sabah)" triggerKey="appointmentReminderSameDay" variables="{musteriAdi}, {randevuSaati}" />
                            </AccordionDetails>
                        </Accordion>

                        <Accordion>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography fontWeight="bold">Finansal Hatırlatmalar</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <SettingItem title="Vadeli Tahsilat Günü" triggerKey="paymentReminder" variables="{musteriAdi}, {tutar}, {vadeTarihi}" />
                            </AccordionDetails>
                        </Accordion>

                        <Box sx={{ mt: 3, textAlign: 'right' }}>
                            <Button variant="contained" onClick={handleSaveSettings} disabled={isSaving}>
                                {isSaving ? <CircularProgress size={24} /> : 'Tüm Ayarları Kaydet'}
                            </Button>
                        </Box>
                    </Paper>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>Test Paneli</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Bu panelden gönderilen tüm test SMS'leri sabit olarak <b>530 687 81 09</b> numarasına gider.
                        </Typography>
                        <TextField fullWidth multiline rows={4} label="Test Mesajı İçeriği" value={testMessage} onChange={(e) => setTestMessage(e.target.value)} sx={{ mb: 2 }} />
                        <Button fullWidth variant="outlined" onClick={handleSendTestSms} disabled={isTesting}>
                            {isTesting ? <CircularProgress size={24} /> : 'Test SMS\'i Gönder'}
                        </Button>
                        {testResult.message && (
                            <Alert severity={testResult.status} sx={{ mt: 2 }}>{testResult.message}</Alert>
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default SmsYonetimi;
